import { idbDelete, idbGetAll, idbPut, STORE_QUEUE } from './offlineDb';
import { removeOfflineOrder } from './offlineOrders';
import { newClientId } from '../utils/clientId';

/**
 * Cola de lo que la caja hizo sin red y falta mandar al servidor.
 *
 * Cada entrada guarda los argumentos con los que la pantalla llamó a su endpoint de RTK
 * Query, con los mismos Ids que generó el cliente. Sincronizar es volver a llamar a ese
 * endpoint con ellos: si alguna alcanzó a llegar antes de cortarse la red, el servidor la
 * reconoce y no la duplica (F0, docs/modo-offline.md). Se reusan los endpoints y no un
 * fetch propio para que la sincronización herede el token, su renovación y la
 * invalidación de caché que ya tiene la app.
 *
 * Se mandan **en orden**: la orden se crea antes de que le entren renglones, y se cobra
 * al final. Si una falla por un rechazo del servidor (4xx), se marca `failed` y va a la
 * lista de "por revisar"; las de esa misma orden se quedan esperando, porque mandar el
 * cobro de una orden que no existe no arregla nada.
 */
export type OfflineOperation =
  | 'openTable' | 'createOrder' | 'addItems' | 'payment'
  | 'createTakeOut' | 'createDelivery'
  /** Precuenta, reabrir o liberar una mesa cuya orden se tomó sin red. */
  | 'tableStatus';

export interface OfflineQueueEntry {
  id: string;
  /** Orden en que ocurrieron; es el orden en que se sincronizan. */
  seq: number;
  operation: OfflineOperation;
  /** Argumentos del endpoint, listos para volver a llamarlo. */
  args: Record<string, unknown>;
  /** Hora real (reloj corregido), la misma que viaja en el cuerpo como occurredAt. */
  occurredAt: string;
  orderId: string;
  tableId?: string;
  /** Para la lista de "por revisar": "Mesa 12", "Cobro mesa 12". */
  label: string;
  status: 'pending' | 'failed';
  /** Mensaje del servidor cuando lo rechazó. */
  error?: string;
  failedAt?: string;
}

export type NewOfflineEntry = Omit<OfflineQueueEntry, 'id' | 'seq' | 'status'>;

const bySeq = (a: OfflineQueueEntry, b: OfflineQueueEntry) => a.seq - b.seq;

// Aviso de cambios para la UI. La cola vive en IndexedDB (lectura asíncrona), así que lo
// que se publica es un contador de versión: quien escuche vuelve a leer.
let version = 0;
const listeners = new Set<() => void>();

export const subscribeOfflineQueue = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

export const getOfflineQueueVersion = (): number => version;

const bump = () => {
  version++;
  listeners.forEach((listener) => listener());
};

/** El `seq` no se reinicia: es el tiempo en milisegundos, que solo avanza. */
let lastSeq = 0;
const nextSeq = (): number => {
  const now = Date.now();
  lastSeq = now > lastSeq ? now : lastSeq + 1;
  return lastSeq;
};

export async function enqueueOffline(entry: NewOfflineEntry): Promise<OfflineQueueEntry> {
  const stored: OfflineQueueEntry = { ...entry, id: newClientId(), seq: nextSeq(), status: 'pending' };
  await idbPut(STORE_QUEUE, stored);
  bump();
  return stored;
}

export async function listOfflineQueue(): Promise<OfflineQueueEntry[]> {
  try {
    return (await idbGetAll<OfflineQueueEntry>(STORE_QUEUE)).sort(bySeq);
  } catch {
    return [];
  }
}

export const listPendingOffline = async (): Promise<OfflineQueueEntry[]> =>
  (await listOfflineQueue()).filter((e) => e.status === 'pending');

export const listFailedOffline = async (): Promise<OfflineQueueEntry[]> =>
  (await listOfflineQueue()).filter((e) => e.status === 'failed');

export async function markOfflineEntryFailed(entry: OfflineQueueEntry, error: string): Promise<void> {
  await idbPut(STORE_QUEUE, { ...entry, status: 'failed', error, failedAt: new Date().toISOString() });
  bump();
}

/**
 * Reintenta TODO lo rechazado de una mesa, nunca una operación suelta.
 *
 * Reintentar solo el cobro cerraba la orden sin sus productos: el servidor acepta pagar de
 * más (lo toma como cambio), así que la cuenta se cerraba en $0 con el importe entero como
 * cambio, y los renglones ya no se podían agregar ("Cannot add items to a closed order").
 */
export async function retryOfflineOrder(orderId: string): Promise<void> {
  const failed = (await listOfflineQueue()).filter((e) => e.orderId === orderId && e.status === 'failed');
  for (const entry of failed) {
    await idbPut(STORE_QUEUE, { ...entry, status: 'pending', error: undefined, failedAt: undefined });
  }
  bump();
}

/** Se llama al sincronizar con éxito. */
export async function removeOfflineEntry(id: string): Promise<void> {
  await idbDelete(STORE_QUEUE, id);
  bump();
}

/**
 * Descarta lo rechazado de una mesa: el usuario decidió que eso no se va a registrar. Se
 * borra también la copia local de la orden cuando ya no le queda nada en la cola; si no,
 * la mesa se quedaba ocupada (o sucia) para siempre en esta computadora, sin forma de
 * volver a abrirla sin conexión.
 */
export async function discardOfflineOrder(orderId: string): Promise<void> {
  const entries = (await listOfflineQueue()).filter((e) => e.orderId === orderId);
  for (const entry of entries) {
    await idbDelete(STORE_QUEUE, entry.id);
  }
  await removeOfflineOrder(orderId);
  bump();
}
